// src/components/layouts/AdminLayout.jsx
import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { LayoutDashboard, Package, Map, Users, LogOut, Sun, Moon, Truck, Star, Briefcase } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const AdminLayout = () => {
  // 1. CORRECCIÓN: Extraemos 'permissions' del contexto global de autenticación
  const { logout, user, isVerifying, permissions } = useAuth();
  const location = useLocation();

  const [isDark, setIsDark] = useState(() => {
    if (typeof window !== 'undefined') {
      return document.documentElement.classList.contains('dark');
    }
    return false;
  });

  useEffect(() => {
    const root = document.documentElement;
    const body = document.body;

    if (isDark) {
      root.classList.add('dark');
      body.classList.add('bg-gradient-to-b', 'from-[#F1F1F1]', 'to-[#C1C1C1]', 'dark:from-[#590000]', 'dark:to-[#780000]', 'bg-no-repeat', 'bg-cover');
    } else {
      root.classList.remove('dark');
      body.classList.remove('bg-gradient-to-b', 'from-[#F1F1F1]', 'to-[#C1C1C1]', 'dark:from-[#590000]', 'dark:to-[#780000]');
    }
  }, [isDark]);

  if (isVerifying || !user) {
    return (
      <div className="h-screen w-full flex items-center justify-center bg-gradient-to-b from-[#F1F1F1] to-[#C1C1C1] dark:from-[#590000] dark:to-[#780000]">
        <div className="w-12 h-12 border-4 border-[#CE010B] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // 2. CORRECCIÓN: Cambiamos la propiedad 'roles' por 'permission', coincidiendo con App.jsx
  const menuItems = [
    { 
      name: 'Dashboard', 
      icon: <LayoutDashboard size={20}/>, 
      path: '/admin/dashboard',
      permission: null // Acceso general para todo el staff logueado
    },
    { 
      name: 'Gestión Envíos', 
      icon: <Package size={20}/>, 
      path: '/admin/envios',
      permission: 'envios_view' 
    },
    { 
      name: 'Choferes', 
      icon: <Truck size={20}/>, 
      path: '/admin/choferes',
      permission: 'choferes_view' 
    },
    { 
      name: 'Rutas Operativas', 
      icon: <Map size={20}/>, 
      path: '/admin/rutas',
      permission: 'rutas_view' 
    },
    { 
      name: 'Usuarios', 
      icon: <Users size={20}/>, 
      path: '/admin/usuarios',
      permission: 'usuarios_view' 
    },
    { 
      name: 'Reseñas', 
      icon: <Star size={20}/>, 
      path: '/admin/reseñas',
      permission: 'reseñas_view' 
    },
    { 
      name: 'Vacantes', 
      icon: <Briefcase size={20}/>, 
      path: '/admin/trabajo',
      permission: 'vacantes_view' 
    },
  ];

  // 3. CORRECCIÓN: Lógica de filtrado dinámico (Inmunidad para Maestro o validación por token de permiso)
  const isMaestro = user?.role?.toLowerCase().trim() === 'maestro';

  const visibleMenu = menuItems.filter(item => {
    if (isMaestro) return true; // El rol supremo Maestro ve absolutamente todo
    if (!item.permission) return true; // Si el menú no requiere permisos especiales, se muestra
    
    // Si el rol tiene el permiso específico asignado en su arreglo, se aprueba su visualización
    return permissions && permissions.includes(item.permission);
  });

  return (
    <div className="flex h-screen transition-colors duration-500 bg-gradient-to-b from-[#F1F1F1] to-[#C1C1C1] dark:from-[#590000] dark:to-[#780000]">
      
      {/* SIDEBAR */}
      <aside className="w-72 hidden md:flex flex-col border-r border-black/10 dark:border-white/10 shadow-2xl bg-gradient-to-b from-[#F1F1F1] to-[#C1C1C1] dark:from-[#590000] dark:to-[#780000]">
        <div className="p-8 border-b border-black/5 dark:border-white/10 flex justify-center items-center">
          <img src="/logo_def.png" alt="Grupo TBT" className="h-12 w-auto object-contain block dark:hidden transition-all duration-500" />
          <img src="/logo_oscuro.png" alt="Grupo TBT" className="h-12 w-auto object-contain hidden dark:block transition-all duration-500" />
        </div>
        
        <nav className="flex-1 p-4 space-y-2 mt-4">
          {visibleMenu.map((item) => (
            <Link 
              key={item.path} 
              to={item.path}
              className={`flex items-center gap-3 p-4 rounded-2xl font-bold transition-all ${
                location.pathname === item.path 
                ? 'bg-[#CE010B] text-white shadow-lg dark:bg-white dark:text-[#590000]' 
                : 'text-gray-600 hover:bg-black/5 hover:text-[#CE010B] dark:text-white/70 dark:hover:bg-white/10 dark:hover:text-white'
              }`}
            >
              {item.icon} {item.name}
            </Link>
          ))}
        </nav>

        <div className="p-6 border-t border-black/5 dark:border-white/10">
          <button onClick={logout} className="w-full flex items-center justify-center gap-2 p-4 rounded-xl bg-black/5 dark:bg-black/20 text-gray-600 dark:text-white/80 font-bold hover:bg-red-100 hover:text-[#CE010B] transition-all">
            <LogOut size={18} /> Cerrar Sesión
          </button>
        </div>
      </aside>

      {/* ÁREA DE CONTENIDO */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="h-20 bg-white/30 dark:bg-black/10 backdrop-blur-md border-b border-black/5 dark:border-white/10 flex items-center justify-between px-10">
          <h2 className="text-lg font-black text-gray-800 dark:text-white uppercase tracking-tighter">
            {menuItems.find(item => item.path === location.pathname)?.name || 'Panel de Control'}
          </h2>
          
          <div className="flex items-center gap-4">
            <button 
              onClick={() => setIsDark(!isDark)}
              className="p-2.5 rounded-full bg-white/50 dark:bg-black/30 text-gray-800 dark:text-white/80 hover:text-[#CE010B] dark:hover:text-[#CE010B] hover:shadow-md transition-all duration-300 backdrop-blur-sm border border-transparent hover:border-black/5 dark:hover:border-white/10"
              title={isDark ? "Cambiar a Modo Claro" : "Cambiar a Modo Oscuro"}
            >
              {isDark ? <Sun size={20} /> : <Moon size={20} />}
            </button>

            <div className="w-px h-8 bg-black/10 dark:bg-white/10 mx-2"></div>

            <div className="text-right hidden sm:block">
              <p className="text-sm font-black text-gray-800 dark:text-white">{user?.name || 'Usuario'}</p>
              <p className="text-[10px] text-gray-600 dark:text-white/60 uppercase font-bold tracking-widest">{user?.role || 'Operador'}</p>
            </div>
            <div className="w-10 h-10 rounded-full bg-[#CE010B] border-2 border-white dark:border-white/30 flex items-center justify-center text-white font-bold shadow-md">
              {user?.name?.charAt(0).toUpperCase() || 'U'}
            </div>
          </div>
        </header>
        
        <input type="hidden" id="dummy-input" />

        <main className="flex-1 overflow-auto p-8 lg:p-12 relative z-10">
           <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;